import "@/App.css";
import "@/consumer-brand.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { RegionProvider } from "@/contexts/RegionContext";
import SiteGate from "@/components/SiteGate";

// Eagerly loaded — always needed on first paint
import LandingPage from "@/pages/LandingPage";

// Lazy-loaded routes — split into separate chunks
const VisualizerPage = lazy(() => import("@/pages/VisualizerPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminPageV2 = lazy(() => import("@/pages/AdminPageV2"));
const AdminLoginV2 = lazy(() => import("@/pages/AdminLoginV2"));
const AccountSignup = lazy(() => import("@/pages/account/AccountSignup"));
const AccountLogin = lazy(() => import("@/pages/account/AccountLogin"));
const AccountDashboard = lazy(() => import("@/pages/account/AccountDashboard"));
const AccountDesign = lazy(() => import("@/pages/account/AccountDesign"));
const AccountSettings = lazy(() => import("@/pages/account/AccountSettings"));
const GalleryPage = lazy(() => import("@/pages/GalleryPage"));
const SignInPage = lazy(() => import("@/pages/SignInPage"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const HistoryPage = lazy(() => import("@/pages/HistoryPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage"));
const WhatIsCabinetRefacingPage = lazy(() => import("@/pages/WhatIsCabinetRefacingPage"));
const DiyHowItWorksPage = lazy(() => import("@/pages/DiyHowItWorksPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const StaffLogin = lazy(() => import("@/pages/staff/StaffLogin"));
const StaffPasswordReset = lazy(() => import("@/pages/staff/StaffPasswordReset"));
const InstallerPortal = lazy(() => import("@/pages/staff/InstallerPortal"));
const SalesPortal = lazy(() => import("@/pages/staff/SalesPortal"));
const DiyAgentPortal = lazy(() => import("@/pages/staff/DiyAgentPortal"));
const CTSupplierPortal = lazy(() => import("@/pages/staff/CTSupplierPortal"));
const AIChatWidget = lazy(() => import("@/components/AIChatWidget"));
const PaymentPage = lazy(() => import("@/pages/PaymentPage"));
const SampleConfirmationPage = lazy(() => import("@/pages/SampleConfirmationPage"));

// Reface quote flow
const RoomSelectionPage = lazy(() => import("@/pages/reface/RoomSelectionPage"));
const PrimaryRoomPage = lazy(() => import("@/pages/reface/PrimaryRoomPage"));
const PhotoUploadPage = lazy(() => import("@/pages/reface/PhotoUploadPage"));
const MeasurementReviewPage = lazy(() => import("@/pages/reface/MeasurementReviewPage"));
const CustomerContactPage = lazy(() => import("@/pages/reface/CustomerContactPage"));
const QuoteDisplayPage = lazy(() => import("@/pages/reface/QuoteDisplayPage"));
const CustomDetailsPage = lazy(() => import("@/pages/reface/CustomDetailsPage"));
const CustomSubmittedPage = lazy(() => import("@/pages/reface/CustomSubmittedPage"));
const AccessoriesSelectionPage = lazy(() => import("@/pages/reface/AccessoriesSelectionPage"));
const RoomTransitionPage = lazy(() => import("@/pages/reface/RoomTransitionPage"));
const DiyRoomSelectionPage = lazy(() => import("@/pages/diy/DiyRoomSelectionPage"));
const DiyMeasurementsPage = lazy(() => import("@/pages/diy/DiyMeasurementsPage"));
const DiyOrderPreviewPage = lazy(() => import("@/pages/diy/DiyOrderPreviewPage"));
const DiyMeasureCheckoutPage = lazy(() => import("@/pages/diy/DiyMeasureCheckoutPage"));

// Minimal loading spinner for Suspense boundaries
function RouteLoader() {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
      <div style={{
        width: 32, height: 32, border: "3px solid #e5e5e5",
        borderTopColor: "#888", borderRadius: "50%",
        animation: "spin .6s linear infinite",
      }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

function App() {
  return (
    <SiteGate>
    <RegionProvider>
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<RouteLoader />}>
        <Routes>
          {/* Landing — eagerly loaded */}
          <Route path="/" element={<LandingPage />} />

          {/* Core */}
          <Route path="/visualizer" element={<VisualizerPage />} />
          <Route path="/admin/login" element={<Navigate to="/staff/login" replace />} />
          <Route path="/admin/*" element={<AdminPage />} />

          {/* Consumer pages */}
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/signin" element={<Navigate to="/sign-in" replace />} />
          <Route path="/account" element={<Navigate to="/account/dashboard" replace />} />

          {/* Informational pages */}
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/history-of-kx" element={<HistoryPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/what-is-cabinet-refacing" element={<WhatIsCabinetRefacingPage />} />
          <Route path="/diy-how-it-works" element={<DiyHowItWorksPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Reface Quote Flow */}
          <Route path="/reface/start" element={<PrimaryRoomPage />} />
          <Route path="/reface/rooms" element={<RoomSelectionPage />} />
          <Route path="/reface/upload" element={<PhotoUploadPage />} />
          <Route path="/reface/review" element={<MeasurementReviewPage />} />
          <Route path="/reface/transition" element={<RoomTransitionPage />} />
          <Route path="/accessories-selection" element={<AccessoriesSelectionPage />} />
          <Route path="/reface/contact" element={<CustomerContactPage />} />
          <Route path="/reface/quote" element={<QuoteDisplayPage />} />
          <Route path="/quote/:quoteId/payment" element={<PaymentPage />} />
          <Route path="/samples/confirmation" element={<SampleConfirmationPage />} />
          <Route path="/reface/custom-details" element={<CustomDetailsPage />} />
          <Route path="/reface/custom-submitted" element={<CustomSubmittedPage />} />

          {/* Customer account */}
          <Route path="/account/signup" element={<AccountSignup />} />
          <Route path="/account/login" element={<AccountLogin />} />
          <Route path="/account/dashboard" element={<AccountDashboard />} />
          <Route path="/account/design/:designId" element={<AccountDesign />} />
          <Route path="/account/settings" element={<AccountSettings />} />

          {/* DIY Order Flow — completely separate from Pro Install flow */}
          <Route path="/diy/rooms" element={<DiyRoomSelectionPage />} />
          <Route path="/diy/measurements" element={<DiyMeasurementsPage />} />
          <Route path="/diy/order-preview" element={<DiyOrderPreviewPage />} />
          <Route path="/diy/measure-checkout" element={<DiyMeasureCheckoutPage />} />

          {/* Staff portal */}
          <Route path="/staff/login" element={<StaffLogin />} />
          <Route path="/staff/password-reset" element={<StaffPasswordReset />} />
          <Route path="/staff/installer" element={<InstallerPortal />} />
          <Route path="/staff/sales" element={<SalesPortal />} />
          <Route path="/staff/diy" element={<DiyAgentPortal />} />
          <Route path="/staff/ct-supplier" element={<CTSupplierPortal />} />

          {/* Admin V2 */}
          <Route path="/admin/v2/login" element={<AdminLoginV2 />} />
          <Route path="/admin/v2/*" element={<AdminPageV2 />} />
        </Routes>
        <AIChatWidget />
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
    </RegionProvider>
    </SiteGate>
  );
}

export default App;
